import { EveningRules, Family, FamilyEvent, TaskTemplate } from '../types/family';
import { OccurrenceState } from '../types/activity';
import { ExceptionRequest, ExcuseKey } from '../types/shared';
import { DEMO_DATE } from '../data/demo';
import { DEFAULT_OCC } from './db';
import { dayOfWeek } from './time';

export type Relation = 'moje' | 'ponujeno' | 'predano' | 'prevzeto';

export interface TodayItem {
  template: TaskTemplate;
  occ: OccurrenceState;
  relation: Relation;
  counterpartId?: string;
  excused: boolean;
  voluntary: boolean;
}

export function excuseKeyOf(t: TaskTemplate): ExcuseKey | undefined {
  if (t.planKey) return t.planKey;
  return t.group === 'hisa' ? 'hisa' : undefined;
}

export function activeExcuses(exceptions: ExceptionRequest[], familyId: string, memberId: string, date: string): ExcuseKey[] {
  return exceptions
    .filter((e) => e.familyId === familyId && e.memberId === memberId && e.status === 'odobreno' && e.from <= date && e.to >= date)
    .flatMap((e) => e.excuses);
}

/** Pouk lastnega instrumenta se prepozna po vrsti dogodka in udeležencu, ne po imenu. */
export function hasLessonOn(family: Family, memberId: string, date: string = DEMO_DATE): boolean {
  return family.events.some((e) => e.type === 'pouk-instrumenta' && e.date === date && e.participantIds.includes(memberId));
}

export const skipKey = (familyId: string, eventId: string, memberId: string) => `${familyId}:${eventId}:${memberId}`;

/** Športni termini iz koledarja (npr. plavanje) v obdobju, razen tistih, ki jih je član označil kot »ni bilo«. */
export function sportEventsFor(family: Family, memberId: string, skipped: string[], from: string, to: string): FamilyEvent[] {
  return family.events.filter(
    (e) =>
      e.type === 'sport' &&
      e.participantIds.includes(memberId) &&
      e.date >= from &&
      e.date <= to &&
      !skipped.includes(skipKey(family.id, e.id, memberId))
  );
}

export function isDueOn(t: TaskTemplate, date: string): boolean {
  if (t.frequency?.kind === 'dogovor') return t.nextDue !== undefined && t.nextDue <= date;
  return !t.nextDue || t.nextDue <= date;
}

/** Dejanski izvajalci izvedbe: predani delež prevzame prevzemnik. */
export function performersOf(t: TaskTemplate, o: OccurrenceState): string[] {
  if (t.assigneeIds.length === 0) return o.performerId ? [o.performerId] : [];
  const ids = t.assigneeIds.map((id) => (o.takenBy && o.offer?.byId === id ? o.takenBy : id));
  return Array.from(new Set(ids));
}

export function isGivenAway(o: OccurrenceState | undefined, memberId: string): boolean {
  return Boolean(o?.takenBy && o.offer?.byId === memberId);
}

export function todayItemsFor(
  family: Family,
  memberId: string,
  occMap: Record<string, OccurrenceState>,
  exceptions: ExceptionRequest[]
): TodayItem[] {
  const ex = activeExcuses(exceptions, family.id, memberId, DEMO_DATE);
  const lesson = hasLessonOn(family, memberId);
  const saturday = dayOfWeek(DEMO_DATE) === 5;
  const out: TodayItem[] = [];
  family.templates.forEach((t) => {
    if (!family.modules[t.module] || !isDueOn(t, DEMO_DATE)) return;
    if (t.group === 'hisa' && t.valueType === 'bonus' && saturday) return;
    const o = occMap[t.id] ?? DEFAULT_OCC;
    let relation: Relation | null = null;
    let counterpartId: string | undefined;
    if (t.assigneeIds.includes(memberId)) {
      if (o.takenBy && o.offer?.byId === memberId) {
        relation = 'predano';
        counterpartId = o.takenBy;
      } else if (o.offer && !o.takenBy && o.offer.byId === memberId) {
        relation = 'ponujeno';
        counterpartId = o.offer.toId;
      } else relation = 'moje';
    } else if (o.takenBy === memberId) {
      relation = 'prevzeto';
      counterpartId = o.offer?.byId;
    }
    if (!relation) return;
    const key = excuseKeyOf(t);
    out.push({
      template: t,
      occ: o,
      relation,
      counterpartId,
      excused: Boolean(key && ex.includes(key)),
      voluntary: t.planKey === 'instrument' && lesson,
    });
  });
  return out;
}

export function eveningLabel(rules: EveningRules, date: string): string {
  const d = dayOfWeek(date);
  if (d === 4 || d === 5) return `V sobi do ${rules.weekendRoom} (zaželeno), spat ob ${rules.weekendSleep}`;
  return `V sobi ob ${rules.schoolNightRoom}, potem je dovoljeno branje`;
}
